import { useEffect, useRef, useState } from 'react'

import type { UIAction } from '../../lib/agent'
import { useAgent } from '../../lib/agent'

/**
 * The assistant (04 §2, 01 §3.5).
 *
 * A sheet, not a page. The room photo is the product; the chat is a way to talk to it, and it
 * should never be what a homeowner has to look at to get anything done — everything the agent
 * can do is also a button somewhere.
 *
 * Two details that matter more than they look:
 *
 *  * **The reply streams.** A blank box for six seconds reads as broken.
 *  * **Tool activity is named.** "Looking through the catalogue…" is honest and it is the
 *    difference between a pause that feels like thinking and one that feels like a hang.
 */

const STARTERS = [
  'Show me what a white vanity would look like',
  'What would this room cost to redo?',
  'I want it to feel like a spa',
  'What baseboard goes with this?',
]

const TOOL_LABEL: Record<string, string> = {
  search_inventory: 'Looking through the catalogue…',
  get_product: 'Reading the product details…',
  render: 'Starting a preview…',
  style_directions: 'Rendering a few directions…',
  estimate_quantity: 'Working out how much you need…',
  cart_add: 'Adding it to your list…',
  cart_view: 'Checking your list…',
  coordination_check: 'Seeing what goes with it…',
  custom_request: 'Noting that for Andrew…',
  availability_lookup: "Checking Andrew's diary…",
  catalog_context: 'Getting my bearings…',
}

interface Props {
  sessionId: string
  open: boolean
  onClose: () => void
  roomId: string | null
  maskId: string | null
  onActions: (actions: UIAction[]) => void
  disabled?: boolean
}

export function AgentSheet({
  sessionId,
  open,
  onClose,
  roomId,
  maskId,
  onActions,
  disabled,
}: Props) {
  const { messages, busy, tool, send, load } = useAgent(sessionId)
  const [draft, setDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const loaded = useRef(false)

  useEffect(() => {
    if (open && !loaded.current) {
      loaded.current = true
      void load()
    }
  }, [open, load])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages])

  if (!open) return null

  const submit = (text: string) => {
    setDraft('')
    void send(text, { roomId, maskId }, onActions)
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-navy/40 sm:items-end sm:justify-center sm:p-6">
      <button
        type="button"
        aria-label="Close the assistant"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-heading"
        className="relative flex max-h-[86vh] min-h-[60vh] flex-col rounded-t-[20px] bg-paper shadow-lift sm:max-h-[80vh] sm:w-[460px] sm:rounded-card"
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 id="agent-heading" className="text-xl">
              Ask about your room
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 min-w-11 rounded-btn border border-line bg-white px-3 text-sm font-semibold text-ink"
          >
            Done
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {messages.length === 0 && (
            <div>
              <p className="m-0 text-sm text-muted">
                Tell me what you have in mind, or start with one of these.
              </p>
              <ul className="m-0 mt-3 grid list-none gap-2 p-0">
                {STARTERS.map((starter) => (
                  <li key={starter}>
                    <button
                      type="button"
                      onClick={() => submit(starter)}
                      className="min-h-11 w-full rounded-btn border border-line bg-white px-3.5 text-left text-sm text-ink hover:border-blue"
                    >
                      {starter}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul className="m-0 grid list-none gap-3 p-0">
            {messages.map((message) => (
              <li
                key={message.id}
                className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
              >
                <div
                  className={`max-w-[85%] rounded-card px-4 py-2.5 text-sm leading-relaxed ${
                    message.role === 'user'
                      ? 'bg-navy text-white'
                      : 'border border-line bg-white text-ink'
                  }`}
                >
                  {message.content || (message.pending ? <Thinking label={tool} /> : null)}
                </div>
              </li>
            ))}
          </ul>
          <div ref={bottomRef} />
        </div>

        <form
          className="border-t border-line bg-white px-5 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            submit(draft)
          }}
        >
          <div className="flex gap-2">
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="What would you like to change?"
              aria-label="Message the assistant"
              disabled={busy || disabled}
              className="min-h-12 flex-1 rounded-btn border border-line bg-white px-3.5 text-base text-ink placeholder:text-muted disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={busy || disabled || !draft.trim()}
              className="min-h-12 rounded-btn bg-blue px-5 font-semibold text-white disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function Thinking({ label }: { label: string | null }) {
  return (
    <span className="inline-flex items-center gap-2 text-muted">
      <span className="flex gap-1" aria-hidden="true">
        <Dot delay="0ms" />
        <Dot delay="140ms" />
        <Dot delay="280ms" />
      </span>
      <span className="text-xs">{label ? (TOOL_LABEL[label] ?? 'Working…') : 'Thinking…'}</span>
    </span>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block size-1.5 animate-pulse rounded-full bg-line-2"
      style={{ animationDelay: delay }}
    />
  )
}
